// Coverage lines panel — FE-SCRM-015.
//
// Drops onto Opportunity detail (sibling to BorPanel + the future
// StakeholderMapPanel mount). Renders the table of coverage lines for
// one opportunity with line_type / quote_status / outcome chips and
// target-carrier pills, plus inline add / edit / delete via modals.
//
// Drop into any opportunity-scoped surface:
//
//   <CoverageLinesPanel opportunityId={opp.opportunity_id} />

import { useState } from "react";

import { Card, Table, TBody, THead, Th, Td, Toast, Tr } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { AddCoverageLineModal } from "./AddCoverageLineModal";
import { EditCoverageLineModal } from "./EditCoverageLineModal";
import {
  COVERAGE_LINE_OUTCOME_LABEL,
  COVERAGE_LINE_OUTCOME_TONE,
  COVERAGE_LINE_TYPE_LABEL,
  QUOTE_STATUS_LABEL,
  formatPremium,
} from "./labels";
import {
  useAddCoverageLineMutation,
  useCoverageLinesQuery,
  useDeleteCoverageLineMutation,
  useUpdateCoverageLineMutation,
} from "./hooks";
import type {
  AddCoverageLineInput,
  CoverageLine,
  CoverageLineOutcome,
  CoverageLineType,
  QuoteStatus,
  UpdateCoverageLineInput,
} from "./types";


interface Props {
  opportunityId: string;
}


function formatErr(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong.";
}


export function CoverageLinesPanel({ opportunityId }: Props) {
  const query = useCoverageLinesQuery(opportunityId);
  const addMutation = useAddCoverageLineMutation(opportunityId);
  const updateMutation = useUpdateCoverageLineMutation(opportunityId);
  const deleteMutation = useDeleteCoverageLineMutation(opportunityId);

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CoverageLine | null>(null);
  const [toast, setToast] = useState<
    | null
    | { variant: "success" | "error"; title: string }
  >(null);

  function submitAdd(input: AddCoverageLineInput) {
    addMutation.mutate(input, {
      onSuccess: () => {
        setAddOpen(false);
        setToast({ variant: "success", title: "Coverage line added" });
      },
    });
  }

  function submitUpdate(values: UpdateCoverageLineInput) {
    if (!editTarget) return;
    updateMutation.mutate(
      { coverage_line_id: editTarget.coverage_line_id, values },
      {
        onSuccess: () => {
          setEditTarget(null);
          setToast({ variant: "success", title: "Coverage line saved" });
        },
      },
    );
  }

  function submitDelete() {
    if (!editTarget) return;
    deleteMutation.mutate(editTarget.coverage_line_id, {
      onSuccess: () => {
        setEditTarget(null);
        setToast({ variant: "success", title: "Coverage line removed" });
      },
      onError: () =>
        setToast({ variant: "error", title: "Couldn't remove" }),
    });
  }

  if (query.isLoading) {
    return (
      <Card data-testid="coverage-lines">
        <DashboardLoadingState variant="card" />
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card data-testid="coverage-lines">
        <DashboardErrorState
          title="Couldn't load coverage lines"
          retry={() => query.refetch()}
        />
      </Card>
    );
  }

  const rows = query.data?.rows ?? [];

  return (
    <Card data-testid="coverage-lines" className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-md font-semibold text-text-primary">
            Coverage lines
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">
            One row per insurance product. Each line wins or loses
            independently.
          </p>
        </div>
        <button
          type="button"
          data-testid="coverage-lines-add-button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse transition-fast ease-out hover:opacity-90"
        >
          + Add coverage line
        </button>
      </header>

      {rows.length === 0 ? (
        <DashboardEmptyState
          title="No coverage lines yet"
          description="Add a coverage line to track the carriers you're quoting."
        />
      ) : (
        <Table
          data-testid="coverage-lines-table"
          className="min-w-[640px]"
          wrapperClassName="rounded-md border border-border-subtle"
        >
          <THead className="bg-recessed/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
            <Tr>
              <Th>Line</Th>
              <Th>Current carrier</Th>
              <Th>Premium</Th>
              <Th>Lives</Th>
              <Th>Quoting</Th>
              <Th>Status</Th>
              <Th>Outcome</Th>
              <Th></Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <CoverageLineRow
                key={row.coverage_line_id}
                row={row}
                onEdit={setEditTarget}
              />
            ))}
          </TBody>
        </Table>
      )}

      <AddCoverageLineModal
        open={addOpen}
        isSubmitting={addMutation.isPending}
        errorMessage={
          addMutation.isError ? formatErr(addMutation.error) : null
        }
        onClose={() => setAddOpen(false)}
        onSubmit={submitAdd}
      />

      <EditCoverageLineModal
        open={editTarget !== null}
        initial={editTarget}
        isSubmitting={updateMutation.isPending}
        isDeleting={deleteMutation.isPending}
        errorMessage={
          updateMutation.isError ? formatErr(updateMutation.error) : null
        }
        onClose={() => setEditTarget(null)}
        onSubmit={submitUpdate}
        onDelete={submitDelete}
      />

      {toast && (
        <Toast
          variant={toast.variant}
          title={toast.title}
          onDismiss={() => setToast(null)}
        />
      )}
    </Card>
  );
}


function CoverageLineRow({
  row, onEdit,
}: {
  row: CoverageLine;
  onEdit: (row: CoverageLine) => void;
}) {
  const lineLabel =
    COVERAGE_LINE_TYPE_LABEL[row.line_type as CoverageLineType]
    ?? row.line_type;
  const quoteLabel =
    QUOTE_STATUS_LABEL[row.quote_status as QuoteStatus]
    ?? row.quote_status;
  const outcomeLabel =
    COVERAGE_LINE_OUTCOME_LABEL[row.outcome as CoverageLineOutcome]
    ?? row.outcome;
  const outcomeTone =
    COVERAGE_LINE_OUTCOME_TONE[row.outcome as CoverageLineOutcome]
    ?? "neutral";

  return (
    <Tr
      data-testid={`coverage-line-row-${row.coverage_line_id}`}
      className="border-t border-border-subtle"
    >
      <Td className="font-medium text-text-primary">
        {lineLabel}
      </Td>
      <Td className="text-text-secondary">
        {row.current_carrier ?? "—"}
      </Td>
      <Td className="text-text-secondary">
        {formatPremium(row.current_premium_annualized_cents) ?? "—"}
      </Td>
      <Td className="text-text-secondary">
        {row.current_lives_covered ?? "—"}
      </Td>
      <Td>
        {row.target_carriers.length === 0 ? (
          <span className="text-text-tertiary">—</span>
        ) : (
          <div
            data-testid={`coverage-line-targets-${row.coverage_line_id}`}
            className="flex flex-wrap gap-1"
          >
            {row.target_carriers.map((c) => (
              <span
                key={c}
                className="rounded-full bg-recessed px-2 py-0.5 text-xs text-text-secondary"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </Td>
      <Td className="text-text-secondary">{quoteLabel}</Td>
      <Td>
        <OutcomeChip
          tone={outcomeTone}
          label={outcomeLabel}
          carrier={
            row.outcome === "won" ? row.outcome_carrier ?? null : null
          }
        />
      </Td>
      <Td className="text-right">
        <button
          type="button"
          data-testid={`coverage-line-edit-${row.coverage_line_id}`}
          onClick={() => onEdit(row)}
          className="rounded-md border border-border-subtle px-2 py-1 text-xs text-text-primary hover:bg-recessed"
        >
          Edit
        </button>
      </Td>
    </Tr>
  );
}


function OutcomeChip({
  tone, label, carrier,
}: {
  tone: "neutral" | "success" | "critical" | "muted";
  label: string;
  carrier: string | null;
}) {
  const toneClass = {
    neutral: "bg-recessed text-text-secondary",
    success: "bg-sage/20 text-sage",
    critical: "bg-ember/15 text-ember",
    muted: "bg-recessed text-text-tertiary",
  }[tone];
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
        toneClass,
      ].join(" ")}
    >
      {label}
      {carrier ? (
        <span className="text-text-tertiary">· {carrier}</span>
      ) : null}
    </span>
  );
}
